import { DataProxy } from 'apollo-cache'
import {
  GetSharesDocument,
  GetSharesQuery,
  GetSharesQueryVariables,
  GetProjectSharesDocument,
  GetProjectSharesQuery,
  GetProjectSharesQueryVariables,
} from 'gqlTypes'

// Here FilterTypes are used in non exhaustive way
// therefore build should not break after SketchQL deployments
// eslint-disable-next-line no-restricted-imports
import { WorkspaceShareFilterType } from 'gqlTypes/expansiveTypes'
import produce from 'immer'
import { ErrorHandler } from 'modules/tracing'

export type SharesPagination = NonNullable<
  GetSharesQuery['workspace']['shares']
>

interface UpdateSharesCache {
  cache: DataProxy
  update: (data: SharesPagination) => void
  workspaceId: string
  filter?: WorkspaceShareFilterType | null
}

export const updateSharesCache = ({
  cache,
  update,
  workspaceId,
  filter = null,
}: UpdateSharesCache) => {
  let existingData: GetSharesQuery | null
  try {
    existingData = cache.readQuery<GetSharesQuery, GetSharesQueryVariables>({
      query: GetSharesDocument,
      variables: {
        identifier: workspaceId,
        after: null,
        search: { name: '', filter },
      },
    })
  } catch (e) {
    ErrorHandler.ignore(e)
    existingData = null
  }

  if (!existingData) return

  const updated = produce(existingData, draft => {
    update(draft.workspace.shares as SharesPagination)
  })

  cache.writeQuery<GetSharesQuery, GetSharesQueryVariables>({
    query: GetSharesDocument,
    data: updated,
    variables: {
      identifier: workspaceId,
      after: null,
      search: { name: '', filter },
    },
  })
}

interface UpdateProjectSharesCache {
  cache: DataProxy
  update: (data: SharesPagination) => void
  projectShortId: string
}

export const updateProjectSharesCache = ({
  cache,
  update,
  projectShortId,
}: UpdateProjectSharesCache) => {
  let existingData: GetProjectSharesQuery | null
  try {
    existingData = cache.readQuery<
      GetProjectSharesQuery,
      GetProjectSharesQueryVariables
    >({
      query: GetProjectSharesDocument,
      variables: {
        shortId: projectShortId,
        after: null,
        search: { name: null, isCurrentVersionDownloadable: null },
      },
    })
  } catch (e) {
    ErrorHandler.ignore(e)
    existingData = null
  }

  if (!existingData) return

  const updated = produce(existingData, draft => {
    update(draft.project.shares as SharesPagination)
  })

  cache.writeQuery<GetProjectSharesQuery, GetProjectSharesQueryVariables>({
    query: GetProjectSharesDocument,
    data: updated,
    variables: {
      shortId: projectShortId,
      after: null,
      search: { name: null, isCurrentVersionDownloadable: null },
    },
  })
}
